import { formatNumber } from "accounting"
import { Flex, Typography } from "antd"
import dayjs from "dayjs"

import Private from "../../../Components/Private"
import Icon from "../../../Icon"
import { formatCurrency } from "../../../Utils/utils"
import { programIcon, programLabel } from "../utils/vocabulary"
import { sortableTitle } from "./sortableTitle"

const { Text } = Typography

export const rateText = (rate, matched, lookups) =>
  rate == null
    ? "-"
    : `${formatNumber(rate, 0, ",", ".")}%${matched == null ? "" : ` (${formatNumber(matched)}/${formatNumber(lookups)})`}`

// Medals for the top three, the plain rank below them.
export const RankMark = ({ rank }) =>
  rank <= 3 ? (
    <Icon type={`medal${rank}`} />
  ) : (
    <Text style={{ display: "inline-block", minWidth: 20, textAlign: "center" }}>{rank}</Text>
  )

// The repeat-cardholder columns, for the card and the full page. On the page the numeric columns
// sort on the server (`sortable`), which is why their dataIndex is repeat-cardholders/'s own
// `ordering` field. Card numbers are masked: only the last four digits are ever shown.
export const repeatColumns = ({ t, sortable = false }) => {
  const title = (label, key, align) => (sortable ? sortableTitle(label, key, { align }) : label)
  const sort = (extra = {}) => (sortable ? { sorter: true, ...extra } : {})

  return [
    {
      title: t("Card"),
      dataIndex: "card_last4",
      key: "card_last4",
      render: (last4, r) => (
        <Flex gap={8} align="center" style={{ minWidth: 0 }}>
          <RankMark rank={r.sequence_number} />
          <Text style={{ fontWeight: 500, fontVariantNumeric: "tabular-nums" }}>
            <Private>{`•••• ${last4}`}</Private>
          </Text>
        </Flex>
      )
    },
    {
      title: t("Most used channel"),
      dataIndex: "top_channel_label",
      key: "top_channel_label",
      render: (label) => t(label)
    },
    {
      title: t("Program"),
      dataIndex: "program",
      key: "program",
      render: (program) => (
        <Flex gap={8} align="center">
          <Icon type={programIcon(program)} style={{ fontSize: 20 }} />
          <Text>{t(programLabel(program))}</Text>
        </Flex>
      )
    },
    {
      title: title(t("Last lookup"), "last_lookup_at"),
      dataIndex: "last_lookup_at",
      key: "last_lookup_at",
      width: 140,
      ...sort(),
      render: (value) => (
        <Text type="secondary" style={{ whiteSpace: "nowrap" }}>
          {value ? dayjs(value).format("DD MMM YYYY") : "-"}
        </Text>
      )
    },
    {
      title: title(t("Lookups"), "lookups_count", "right"),
      dataIndex: "lookups_count",
      key: "lookups_count",
      width: 110,
      align: "right",
      ...sort({ defaultSortOrder: "descend" }),
      render: (value) => <Private>{formatNumber(value)}</Private>
    },
    {
      title: title(t("Matched"), "matched_count", "right"),
      dataIndex: "matched_count",
      key: "matched_count",
      width: 110,
      align: "right",
      ...sort(),
      render: (value) => <Private>{formatNumber(value)}</Private>
    },
    {
      title: title(t("Order value ($)"), "order_amount", "right"),
      dataIndex: "order_amount",
      key: "order_amount",
      width: 150,
      align: "right",
      ...sort(),
      render: (value) => <Private>{formatCurrency(value, true)}</Private>
    },
    {
      title: title(t("Match rate"), "match_rate", "right"),
      dataIndex: "match_rate",
      key: "match_rate",
      width: 140,
      align: "right",
      ...sort(),
      render: (rate, r) => <Private>{rateText(rate, r.matched_count, r.lookups_count)}</Private>
    }
  ]
}

// The fixed columns take 650px; the rest is room for card, channel and program. Below it the
// table scrolls instead of wrapping those three a letter per line.
export const REPEAT_TABLE_SCROLL = { x: 1100 }

// The summary's cells under the columns above, after the card column.
export const REPEAT_SUMMARY_CELLS = [
  null,
  null,
  null,
  { align: "right", render: (s) => <Private>{formatNumber(s.lookups_count)}</Private> },
  { align: "right", render: (s) => <Private>{formatNumber(s.matched_count)}</Private> },
  { align: "right", render: (s) => <Private>{formatCurrency(s.order_amount, true)}</Private> },
  { align: "right", render: (s) => <Private>{rateText(s.match_rate)}</Private> }
]
